/**
 * US phone numbers: what counts as one, and the single way we write it down.
 *
 * Two separate jobs, deliberately not merged:
 *
 *   - BE FORGIVING ABOUT INPUT. People type `5045550199`, `504-555-0199`,
 *     `(504) 555-0199`, `504.555.0199`, `+1 504 555 0199`. Refusing any of
 *     those is refusing a correct phone number over punctuation.
 *   - BE STRICT ABOUT STORAGE. One canonical format, so the same number typed
 *     two ways is the same string in the database — searchable, comparable, and
 *     not two apparent customers.
 *
 * This is a Slidell, Louisiana business and the app has always been US-only
 * (the previous rule required exactly ten digits). Nothing here narrows that;
 * it widens what is accepted on the way in and normalises on the way out.
 */

/** The format every stored number is written in, and the one shown in errors. */
export const PHONE_EXAMPLE = '(504) 555-0199';

/**
 * The ten significant digits, or null when the input is not a US number.
 *
 * A leading `1` on an eleven-digit input is the country code and is dropped —
 * `1-504-555-0199` and `(504) 555-0199` are the same telephone.
 *
 * The final check is the North American Numbering Plan rule: neither the area
 * code nor the exchange may begin with 0 or 1. That is what separates a real
 * number from ten digits, so `1234567890` and `0005550199` are rejected rather
 * than stored as though somebody could be reached on them.
 */
export function phoneDigits(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');

  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (national.length !== 10) return null;

  // NPA (area) and NXX (exchange) must each start 2-9.
  if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(national)) return null;

  return national;
}

/** True for a number this app can store. An empty string is NOT a number. */
export function isValidPhone(raw: string): boolean {
  return phoneDigits(raw) !== null;
}

/**
 * The canonical form, or null when the input is not a valid US number.
 *
 * Returning null rather than the input unchanged keeps the caller honest: a
 * value that cannot be formatted is one that should not be stored, and a
 * function that quietly hands back the garbage it was given makes that easy to
 * miss.
 */
export function formatPhone(raw: string): string | null {
  const digits = phoneDigits(raw);
  if (!digits) return null;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
